import React, { useCallback, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { useFormik } from 'formik'

import UploadAdCreativeCampaignForm from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm'
import NewCampaignBudgetFields from './NewCampaignBudgetFields'
import NewCampaignImpressionsFields from './NewCampaignImpressionsFields'

import useRightBudgetAndImpressionsCalculation from '../../../../Multiplatform/UploadAdCreative/useRightBudgetAndImpressionsCalculation'
import { usePurifiedFormik } from '../../../../../hooks/formHooks/usePurifiedFormik'
import { formatOptionsList } from '../../../../../features/formatters'

import { setRightUploadAdData } from '../../../../../modules/actions/rights'
import { createCampaign } from '../../../../../modules/actions/campaigns'
import { getLineItems } from '../../../../../modules/actions/lineItems'
import { campaignCreateIsLoadingSelector } from '../../../../../modules/selectors/campaigns'
import { selectedDV360AdAccountSelector } from '../../../../../modules/selectors/adAccounts'
import { combinedCampaignsSelector } from '../../../../../modules/selectors/combinedData'
import { lineItemsLoadingSelector } from '../../../../../modules/selectors/lineItems'
import { rightSelector } from '../../../../../modules/selectors/rights'
import { selectedPartnershipSelector } from '../../../../../modules/selectors/partnerships'

import { CAMPAIGN } from '../../../../Multiplatform/UploadAdCreative/UploadAdCreativeCampaignForm/fields'

import { DV_360_PLATFORM, DV_360_PROVIDER_PLATFORM } from '../../../../../constants/selectLists/platformList'
import { LIFETIME_DATE_PRESET } from '../../../../../constants/selectLists/listItemFiltersList'
import { getInitialValues, transformValuesToBE, getValidationSchema } from './fields'
import {
  DIGITAL_ALLOCATION_TYPE_BUDGET,
  DIGITAL_ALLOCATION_TYPE_IMPRESSIONS
} from '../../../../Multiplatform/Partnerships/RightForms/fields'
import { LIST_ITEM_OPTION, OPTION_EXISTING_LIST_ITEM } from '../../../../../hooks/formHooks/listItemFormHooks/fields'
import {
  PERFORMANCE_GOAL_AMOUNT,
  PLANNED_SPEND_AMOUNT
} from '../../../CampaignForms/CampaignDv360Create/CampaignDv360CreateForm/fields'
import { IMPRESSIONS } from '../../../../Multiplatform/UploadAdCreative/fields'
import { FREQUENCY_IMPRESSIONS } from '../../../fields'

const campaignStepValidationFields = [PERFORMANCE_GOAL_AMOUNT, IMPRESSIONS, PLANNED_SPEND_AMOUNT, FREQUENCY_IMPRESSIONS]

const CampaignForm = ({ formProgressItems, onSuccessSubmit }) => {
  const dispatch = useDispatch()

  const { id: selectedDV360AdAccountId, currency_symbol: currencySymbol } = useSelector(selectedDV360AdAccountSelector)

  const combinedCampaigns = useSelector(combinedCampaignsSelector)
  const selectedPartnership = useSelector(selectedPartnershipSelector)

  const right = useSelector(rightSelector)

  const { allocation_type: rightAllocationType } = right

  const isBudgetAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_BUDGET
  const isImpressionAllocationType = rightAllocationType === DIGITAL_ALLOCATION_TYPE_IMPRESSIONS

  const { campaigns = [] } = combinedCampaigns

  const { remainingBudget, remainingImpressions } = useRightBudgetAndImpressionsCalculation()

  const dv360Campaigns = useMemo(
    () => campaigns.filter(campaign => campaign.provider === DV_360_PROVIDER_PLATFORM),
    [campaigns]
  )

  const isCampaignsExist = !!dv360Campaigns.length

  const formattedCampaigns = formatOptionsList({
    list: dv360Campaigns,
    valueName: 'id',
    labelName: 'name'
  })

  const onSubmit = useCallback(
    values => {
      // if existing campaign is selected, find full data and set to redux state + fetch line items
      if (values[LIST_ITEM_OPTION] === OPTION_EXISTING_LIST_ITEM) {
        const selectedCampaignFullData = formattedCampaigns.find(campaign => values[CAMPAIGN] === campaign.id)

        dispatch(setRightUploadAdData({ campaign: selectedCampaignFullData }, DV_360_PLATFORM))

        dispatch(
          getLineItems(
            {
              account: selectedDV360AdAccountId,
              campaign_id: values[CAMPAIGN],
              date_preset: LIFETIME_DATE_PRESET,
              insights: ['impressions']
            },
            DV_360_PLATFORM
          )
        )
      } else {
        const campaignData = transformValuesToBE({
          values,
          right,
          account: selectedDV360AdAccountId,
          selectedPartnership
        })

        dispatch(
          createCampaign({ campaignData }, DV_360_PLATFORM, {
            pushToCombinedCampaignsList: true
          })
        )
      }
    },
    [dispatch, formattedCampaigns, selectedDV360AdAccountId, right, selectedPartnership]
  )

  const formik = useFormik({
    initialValues: getInitialValues({ isCampaignsExist }),
    validationSchema: getValidationSchema({
      remainingBudget,
      remainingImpressions,
      allocationTypeImpressions: isImpressionAllocationType,
      currencySymbol
    }),
    onSubmit
  })
  const purifiedFormik = usePurifiedFormik(formik)
  const { values } = purifiedFormik

  const { [LIST_ITEM_OPTION]: campaignOption } = values

  // when we use existing campaign, we need to pre-load line items for the next LineItem form
  // in this case submit form loads line item. However, we don't do the same for error selectors,
  // because we don't block user for accessing next form when line items get request failed
  const isLoadingSelector =
    campaignOption === OPTION_EXISTING_LIST_ITEM ? lineItemsLoadingSelector : campaignCreateIsLoadingSelector

  return (
    <UploadAdCreativeCampaignForm
      adAccountId={selectedDV360AdAccountId}
      formik={formik}
      platform={DV_360_PLATFORM}
      onSuccessSubmit={onSuccessSubmit}
      isLoadingSelector={isLoadingSelector}
      existedCampaigns={formattedCampaigns}
      formProgressItems={formProgressItems}
      campaignStepValidationFields={campaignStepValidationFields}
    >
      {isBudgetAllocationType && <NewCampaignBudgetFields formik={purifiedFormik} remainingBudget={remainingBudget} />}
      {isImpressionAllocationType && (
        <NewCampaignImpressionsFields formik={purifiedFormik} remainingImpressions={remainingImpressions} />
      )}
    </UploadAdCreativeCampaignForm>
  )
}

CampaignForm.propTypes = {
  formProgressItems: PropTypes.arrayOf(PropTypes.object).isRequired
}

export default CampaignForm
